/* ═══ COLEG AI — shared module ═══════════════════════════════════
 * Maintenance-buddy assistant, active on ALL work flows (Dan, 2026-07-07):
 * urgent interventions (journal.ts), scheduled revisions (workflow.ts) and
 * daily/ad-hoc log entries (tasks-calendar.ts). Backend: /api/colleague/*
 * (aliases of /api/urgent/colleague_*), cloud behind budget gate + local
 * fallback. Imports ONLY from core — safe against import cycles.
 */
import { api, toast, escHtml } from './core';

/** Payload shared by brief/chat/summary calls. */
export interface ColleagueContext {
    context_type?: 'log' | 'workflow';
    log_id?: number;
    workflow_id?: number;
    machine_id?: string;
    problem?: string;
    extra_context?: string;
}

// Budget badge logic duplicated from chat-ai.ts on purpose: importing chat-ai
// here would pull auth-ui and create an import cycle back into journal.ts.
function _updateBudgetBadge(b: any): void {
    if (!b || typeof b.spent_usd !== 'number') return;
    const badge = document.getElementById('cloud-budget-badge');
    if (!badge) return;
    badge.classList.remove('hidden');
    badge.textContent = `☁️ ${b.spent_usd.toFixed(2)}$ / ${b.limit_usd.toFixed(0)}$`;
    badge.title = `Consum AI Cloud luna aceasta: ${b.spent_usd.toFixed(2)}$ din ${b.limit_usd.toFixed(0)}$ (${b.pct}%)`;
    badge.style.color = b.exceeded ? 'var(--red, #e05252)' : (b.warning ? 'var(--orange, #e0a030)' : 'var(--text-muted, #888)');
}

/** Markdown-lite renderer (bold + bullets + line breaks) on escaped text. */
export function colleagueMd(text: string): string {
    let html = escHtml(text || '');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/^[-•*]\s+/gm, '• ');
    return html.replace(/\n/g, '<br>');
}

/** Element id helper: prefix '' keeps the legacy urgent-flow ids. */
function _cid(prefix: string, name: string): string {
    return prefix ? `${prefix}-${name}` : name;
}

const _chatHistories: Record<string, { role: string; text: string }[]> = {};

/** Inner HTML for a colleague card body (brief + chat). Reuses .colleague-* CSS. */
export function colleaguePanelHtml(prefix: string): string {
    return `
        <div id="${_cid(prefix, 'colleague-brief')}" class="colleague-brief">
            <div class="urg-intv-loading">Colegul AI se uită prin istoric și manual...</div>
        </div>
        <div id="${_cid(prefix, 'colleague-chat-thread')}" class="colleague-chat-thread"></div>
        <form id="${_cid(prefix, 'colleague-chat-form')}" class="colleague-chat-form" autocomplete="off">
            <input type="text" id="${_cid(prefix, 'colleague-chat-input')}" class="urg-input"
                   placeholder="Întreabă colegul AI...">
            <button type="submit" class="urg-op-btn urg-op-btn--green">Trimite</button>
        </form>`;
}

/** Load the opening brief into #<prefix>-colleague-brief and reset the thread. */
export async function colleagueLoadBrief(prefix: string, ctx: ColleagueContext): Promise<void> {
    const el = document.getElementById(_cid(prefix, 'colleague-brief'));
    if (!el) return;
    _chatHistories[prefix] = [];
    const thread = document.getElementById(_cid(prefix, 'colleague-chat-thread'));
    if (thread) thread.innerHTML = '';
    el.innerHTML = '<div class="urg-intv-loading">🧑‍🔧 Colegul AI se uită prin istoric și manual... (poate dura până la un minut)</div>';
    try {
        const res = await api('/api/colleague/brief', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(ctx)
        });
        const brief = res.brief || '';
        if (!brief) {
            el.innerHTML = '<div class="urg-intv-loading">Colegul AI nu are nimic de semnalat acum. Îl poți întreba direct mai jos.</div>';
            return;
        }
        const srcLabel = res.source === 'cloud' ? 'AI Cloud (Claude)'
            : res.source === 'cache' ? 'AI Cloud (brief recent, din cache)'
            : (res.source === 'local' ? 'AI Local' : 'date din istoric (AI indisponibil)');
        el.innerHTML = colleagueMd(brief) +
            `<div class="colleague-brief-source">Sursă: ${escHtml(srcLabel)}</div>`;
        if (res.cloud_budget) _updateBudgetBadge(res.cloud_budget);
    } catch (e) {
        el.innerHTML = '<div class="urg-intv-loading">Colegul AI nu e disponibil momentan. Poți continua lucrarea normal.</div>';
    }
}

function _addBubble(prefix: string, role: string, text: string, typing = false): HTMLElement | null {
    const thread = document.getElementById(_cid(prefix, 'colleague-chat-thread'));
    if (!thread) return null;
    const div = document.createElement('div');
    div.className = 'colleague-msg ' + (role === 'user' ? 'colleague-msg--user' : 'colleague-msg--ai') + (typing ? ' typing' : '');
    if (role === 'user') div.textContent = text;
    else div.innerHTML = colleagueMd(text);
    thread.appendChild(div);
    thread.scrollTop = thread.scrollHeight;
    return div;
}

/**
 * Wire the chat form for a panel. getCtx is evaluated per message so callers
 * can bind live state (e.g. current log/workflow id). Returns silently if the
 * form is missing; re-wiring the same form is a no-op (dataset guard).
 */
export function colleagueWireChat(prefix: string, getCtx: () => ColleagueContext | null): void {
    const form = document.getElementById(_cid(prefix, 'colleague-chat-form')) as HTMLFormElement | null;
    if (!form || form.dataset.colleagueWired === '1') return;
    form.dataset.colleagueWired = '1';
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const input = document.getElementById(_cid(prefix, 'colleague-chat-input')) as HTMLInputElement;
        const msg = (input?.value || '').trim();
        if (!msg) return;
        const ctx = getCtx();
        if (!ctx) { toast('Nu există o lucrare activă!', 'error'); return; }
        input.value = '';
        const hist = _chatHistories[prefix] || (_chatHistories[prefix] = []);
        _addBubble(prefix, 'user', msg);
        const typingEl = _addBubble(prefix, 'assistant', 'Colegul AI scrie...', true);
        try {
            const res = await api('/api/colleague/chat', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...ctx, message: msg, history: hist.slice(-6) })
            });
            const answer = res.response || 'Nu am primit răspuns.';
            if (typingEl) { typingEl.classList.remove('typing'); typingEl.innerHTML = colleagueMd(answer); }
            hist.push({ role: 'user', text: msg }, { role: 'assistant', text: answer });
            if (hist.length > 6) _chatHistories[prefix] = hist.slice(-6);
            if (res.cloud_budget) _updateBudgetBadge(res.cloud_budget);
        } catch (err) {
            if (typingEl) {
                typingEl.classList.remove('typing');
                typingEl.textContent = 'Eroare: ' + ((err as Error).message || 'AI indisponibil');
            }
        }
        const thread = document.getElementById(_cid(prefix, 'colleague-chat-thread'));
        if (thread) thread.scrollTop = thread.scrollHeight;
    });
}

export interface ColleagueLessonPayload extends ColleagueContext {
    actions?: string;
    cause?: string;
    parts?: string;
    notes?: string;
}

/**
 * End-of-work lesson offer: ask the colleague for a summary, show the
 * 1-tap modal ([Da salvează] / [Corectează] / [Nu salva]). Fire-and-forget:
 * any failure returns silently so the caller's finish flow is never blocked.
 */
export async function colleagueOfferLesson(payload: ColleagueLessonPayload): Promise<void> {
    let res: any;
    try {
        res = await api('/api/colleague/summary', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
    } catch (_e) { return; } // AI down — never block the finish flow
    const summary = (res && res.summary) ? String(res.summary).trim() : '';
    if (!summary) return;

    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.7);display:flex;align-items:center;justify-content:center;animation:fadeIn .15s ease';
    const box = document.createElement('div');
    box.style.cssText = 'background:var(--surface,#1e293b);border:1px solid var(--border,#334155);border-radius:16px;padding:26px 30px;max-width:560px;width:92%;box-shadow:0 20px 60px rgba(0,0,0,.5);max-height:85vh;max-height:85dvh;overflow-y:auto';
    box.innerHTML = `
        <div style="font-size:1.1rem;font-weight:700;color:var(--text,#fff);margin-bottom:12px">🧑‍🔧 Colegul AI a pregătit un rezumat</div>
        <div style="font-size:.9rem;color:var(--text-muted,#94a3b8);margin-bottom:14px">Îl salvăm ca lecție învățată? Data viitoare, colegii primesc soluția asta direct.</div>
        <div id="_cl_summary" style="font-size:.95rem;line-height:1.6;color:var(--text,#fff);background:rgba(59,130,246,.08);border:1px solid rgba(59,130,246,.2);border-radius:10px;padding:14px;white-space:pre-wrap">${escHtml(summary)}</div>
        <textarea id="_cl_edit" rows="6" style="display:none;width:100%;padding:10px 14px;font-size:.95rem;border-radius:10px;border:1px solid var(--border,#334155);background:rgba(255,255,255,.08);color:var(--text,#fff);resize:vertical;font-family:inherit"></textarea>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:18px;flex-wrap:wrap">
            <button id="_cl_skip" style="padding:10px 18px;font-size:.9rem;font-weight:600;border-radius:10px;border:1px solid var(--border,#334155);background:rgba(255,255,255,.06);color:var(--text,#fff);cursor:pointer">✕ Nu salva</button>
            <button id="_cl_fix" style="padding:10px 18px;font-size:.9rem;font-weight:600;border-radius:10px;border:1px solid rgba(59,130,246,.4);background:rgba(59,130,246,.12);color:var(--text,#fff);cursor:pointer">✏️ Corectează</button>
            <button id="_cl_ok" style="padding:10px 22px;font-size:.9rem;font-weight:700;border-radius:10px;border:none;background:#00e676;color:#000;cursor:pointer">✅ Da, salvează ca lecție</button>
        </div>`;
    overlay.appendChild(box);
    document.body.appendChild(overlay);

    const summaryEl = box.querySelector('#_cl_summary') as HTMLElement;
    const editEl = box.querySelector('#_cl_edit') as HTMLTextAreaElement;
    box.querySelector('#_cl_fix')!.addEventListener('click', () => {
        if (editEl.style.display === 'none') editEl.value = summary;
        summaryEl.style.display = 'none';
        editEl.style.display = 'block';
        editEl.focus();
    });
    box.querySelector('#_cl_skip')!.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
    box.querySelector('#_cl_ok')!.addEventListener('click', async () => {
        const finalSummary = (editEl.style.display !== 'none' ? editEl.value : summary).trim();
        if (!finalSummary) { toast('Rezumatul este gol.', 'warning'); return; }
        const okBtn = box.querySelector('#_cl_ok') as HTMLButtonElement;
        okBtn.disabled = true; okBtn.textContent = '⏳ Se salvează...';
        try {
            await api('/api/colleague/confirm', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    context_type: payload.context_type || 'log',
                    log_id: payload.log_id || 0,
                    workflow_id: payload.workflow_id || 0,
                    summary: finalSummary,
                    machine_id: res.machine_id || payload.machine_id || '',
                    problem: res.problem || payload.problem || '',
                    cause: payload.cause || '', parts: payload.parts || ''
                })
            });
            overlay.remove();
            toast('📚 Lecția a fost salvată — mulțumim! Colegii o vor primi la lucrări similare.', 'success');
        } catch (err) {
            okBtn.disabled = false; okBtn.textContent = '✅ Da, salvează ca lecție';
            toast('Eroare la salvarea lecției: ' + ((err as Error).message || ''), 'error');
        }
    });
}

/* ── One-shot completed-work lesson context handoff (workflow → log modal) ──
 * _wfFinalize (workflow.ts) completes the workflow, then the classic log
 * modal (tasks-calendar.ts) opens. The modal submit picks this up so the
 * lesson offer carries full workflow context. */
let _pendingWorkflowLesson: { workflow_id: number; machine_id: string; problem: string } | null = null;

export function setPendingWorkflowLesson(ctx: { workflow_id: number; machine_id: string; problem: string }): void {
    _pendingWorkflowLesson = ctx;
}

export function takePendingWorkflowLesson(): { workflow_id: number; machine_id: string; problem: string } | null {
    const v = _pendingWorkflowLesson;
    _pendingWorkflowLesson = null;
    return v;
}

/**
 * "🧑‍🔧 Ajută-mă la completare" — ask the colleague to draft text for an
 * observations field, show the proposal with a „Preia" button that fills the
 * target field only on explicit click.
 */
export async function colleagueSuggestField(opts: {
    machine_id: string;
    action: string;
    currentText: string;
    targetEl: HTMLTextAreaElement | HTMLInputElement;
    containerEl: HTMLElement;
}): Promise<void> {
    const { machine_id, action, currentText, targetEl, containerEl } = opts;
    containerEl.innerHTML = '<div class="urg-intv-loading">🧑‍🔧 Colegul AI pregătește o propunere...</div>';
    try {
        const res = await api('/api/colleague/chat', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                machine_id,
                extra_context: `CONTEXT: mecanicul completează observațiile pentru lucrarea „${action}" pe ${machine_id}.`
                    + (currentText ? ` A scris deja: „${currentText}"` : ''),
                message: 'Propune un text scurt (2-4 fraze, la persoana întâi) pentru câmpul „Observații" al acestei lucrări, '
                    + 'pe baza a ce am scris deja și a istoricului mașinii. Doar textul propus, fără alte explicații.',
                history: []
            })
        });
        const proposal = (res.response || '').trim();
        if (!proposal || res.source === 'none') {
            containerEl.innerHTML = '<div class="urg-intv-loading">Colegul AI nu a putut genera o propunere acum.</div>';
            return;
        }
        containerEl.innerHTML = `
            <div class="colleague-brief" style="margin:8px 0">
                ${colleagueMd(proposal)}
                <div style="display:flex;gap:8px;margin-top:10px">
                    <button type="button" class="btn btn-sm" data-cl-take style="background:#00e676;color:#000;font-weight:700">✔ Preia</button>
                    <button type="button" class="btn btn-sm btn-outline" data-cl-dismiss>✕ Renunță</button>
                </div>
            </div>`;
        if (res.cloud_budget) _updateBudgetBadge(res.cloud_budget);
        containerEl.querySelector('[data-cl-take]')?.addEventListener('click', () => {
            targetEl.value = proposal;
            containerEl.innerHTML = '';
            toast('Textul propus a fost preluat — îl poți edita.', 'info');
        });
        containerEl.querySelector('[data-cl-dismiss]')?.addEventListener('click', () => {
            containerEl.innerHTML = '';
        });
    } catch (e) {
        containerEl.innerHTML = '<div class="urg-intv-loading">Colegul AI nu e disponibil momentan.</div>';
    }
}
